import { defaultBlockRegistry } from '../renderer/block-registry.gen.js';
import { collectTextSlots } from './textSlots.js';
/**
 * Sections no category may ever generate. GLOBAL and non-overridable: honesty is not a per-niche
 * preference, and a recipe author must not be able to opt back in by mistake.
 */
export const NEVER_GENERATED = [
    'testimonials',
    'trustpilot-reviews',
    'facebook-testimonials',
    'results',
    'spec-comparison',
    'comparison-table',
    'custom-html',
];
// ─────────────────────────────────────────────────────────────────────────────
// The eight recipes. Roles come from the "ce qui vend dans cette niche" analysis
// each theme was built on (docs/features/0052 → 0058), not from guesswork.
// ─────────────────────────────────────────────────────────────────────────────
const R = (type, role) => ({ type, role });
/**
 * ROLES EVERY CATEGORY INHERITS.
 *
 * Most sections do a job that does not change with the niche: a FAQ is a FAQ, `related-products` is a
 * heading over a list the catalogue fills, `anchor-nav` is a handful of nav labels. Writing those into
 * each recipe by hand is how the first draft of this file left `faq` unwritten on four home pages and —
 * far worse — left the all-purpose Atlas home generating TWO slots, because Atlas uses `slideshow` and
 * `contact-form` where the others use `slideshow-hero`. The measurement caught it; a hand-kept list per
 * category would have kept re-introducing it every time a theme gained a section.
 *
 * A category's own entry OVERRIDES the universal one — that is where niche voice belongs.
 */
const UNIVERSAL_ROLES = {
    // Openers
    'slideshow-hero': 'La promesse de la boutique en une phrase courte et un appel à l’action.',
    slideshow: 'La promesse de la boutique en une phrase courte et un appel à l’action.',
    hero: 'La promesse de la boutique en une phrase courte et un appel à l’action.',
    'parallax-hero': 'La promesse de la boutique en une phrase courte et un appel à l’action.',
    'slideshow-hero-alt': 'La promesse de la boutique en une phrase courte et un appel à l’action.',
    // Navigation et repères
    'anchor-nav': 'Les repères de la page, deux mots chacun, dans l’ordre où on les rencontre.',
    'collection-list': 'Les noms des rayons, courts et clairs.',
    'featured-collection': 'Le nom de la sélection mise en avant et une phrase qui donne envie de l’ouvrir.',
    'featured-product': 'Le produit mis en avant : ce que c’est, et pourquoi celui-là.',
    'related-products': 'Le titre de la rangée de suggestions.',
    // Corps de page
    'image-with-text': 'Pourquoi la boutique existe, et pour qui.',
    'rich-text': 'Un paragraphe qui donne le ton de la boutique.',
    'custom-columns': 'Les points forts, un par colonne, en langage d’usage.',
    'custom-columns-new': 'Les points forts, un par colonne, en langage d’usage.',
    multirow: 'Les usages concrets, un par ligne.',
    'icon-bar': 'Les raisons d’acheter ici : livraison, retour, service, paiement.',
    'icons-with-content': 'Les raisons d’acheter ici, une par icône.',
    'trust-row': 'Les garanties concrètes. Rien d’invérifiable.',
    timeline: 'Les étapes, dans l’ordre, une phrase chacune.',
    collage: 'Les légendes des visuels, courtes.',
    'shoppable-image': 'Les libellés des points cliquables de la scène.',
    'comparison-slider': 'Le titre et la légende de l’avant/après. Ne jamais chiffrer un résultat.',
    'tabbed-content': 'Les onglets et leur contenu, un usage par onglet.',
    'content-tabs': 'Les onglets et leur contenu, un usage par onglet.',
    'collapsible-content': 'Les questions qui retiennent d’acheter, et leur réponse.',
    faq: 'Six questions réelles d’acheteur, avec des réponses courtes et concrètes.',
    'video-with-text': 'Le titre et le texte qui accompagnent la vidéo.',
    'insta-stories': 'Les libellés des vignettes.',
    'horizontal-ticker': 'Les mentions qui défilent, trois à cinq, très courtes.',
    'vertical-ticker': 'Les mentions qui défilent, trois à cinq, très courtes.',
    'logo-list': 'Le titre de la rangée de logos.',
    'contact-form': 'Le titre, l’introduction et les libellés du formulaire de contact.',
    newsletter: 'Une raison honnête de laisser son adresse. Aucun code promo inventé.',
    'announcement-bar': 'Une annonce vraie et courte. Aucune urgence inventée.',
    // Fiche produit
    'main-product': 'Le produit : ce qu’il est, ce qu’il fait, pour qui.',
    'sticky-cta': 'Le rappel d’achat : nom court et libellé de bouton.',
    'benefit-panel': 'Les bénéfices concrets du produit, un par ligne.',
    'bundle-deals': 'Le titre de l’offre groupée et le libellé de chaque lot.',
    'product-gallery': 'Les légendes des visuels.',
    'collection-banner': 'Le titre et l’introduction du rayon.',
    'collection-grid': 'Le titre de la grille et le texte quand elle est vide.',
    'anchor-nav-alt': 'Les repères de la page.',
    'track-order': 'Le titre et l’aide du suivi de commande.',
    'contact-form-alt': 'Le titre et l’introduction du formulaire.',
};
/** Shared shape of a product page: the same six jobs, said differently per niche. */
function productPage(roles) {
    return [
        R('main-product', roles.hero),
        R('trust-row', roles.proof),
        R('custom-columns', roles.detail),
        R('image-with-text', roles.detail),
        R('collapsible-content', roles.objection),
        R('faq', roles.faq),
        R('multirow', roles.detail),
        R('tabbed-content', roles.detail),
        R('icon-bar', roles.proof),
        R('sticky-cta', roles.hero),
    ];
}
export { UNIVERSAL_ROLES };
export const RECIPES = [
    {
        id: 'beauty',
        label: 'Beauté & cosmétique',
        voice: 'Sensoriel et précis. On décrit une texture, un geste, un résultat visible — jamais une promesse médicale.',
        pages: {
            index: [
                R('slideshow-hero', 'La promesse de la marque en une phrase courte et un appel à découvrir.'),
                R('icon-bar', 'Quatre à six raisons d’acheter ici : formulation, origine, tolérance, livraison.'),
                R('featured-collection', 'Le nom du rayon mis en avant et une phrase qui donne envie d’ouvrir.'),
                R('comparison-slider', 'Le titre et la légende d’un avant/après. Ne jamais chiffrer un résultat.'),
                R('image-with-text', 'L’histoire de la marque : pourquoi elle existe, pour qui.'),
                R('collection-list', 'Les noms des rayons, clairs et courts.'),
                R('custom-columns', 'Les engagements de la marque, un par colonne.'),
                R('newsletter', 'Une raison honnête de laisser son adresse. Aucun code promo inventé.'),
            ],
            product: productPage({
                hero: 'Le nom du produit, ce qu’il fait, pour quel type de peau.',
                proof: 'Les garanties concrètes : livraison, retour, paiement. Rien d’invérifiable.',
                detail: 'La texture, le geste d’application, le moment de la routine, la composition.',
                objection: 'Les questions qui retiennent d’acheter : tolérance, parfum, durée du flacon.',
                faq: 'Six questions réelles sur l’usage et la conservation.',
            }),
        },
    },
    {
        id: 'coffee',
        label: 'Café & torréfaction',
        voice: 'Chaleureux et artisanal. On parle d’origine, de torréfaction et de rituel, pas de performance.',
        pages: {
            index: [
                R('slideshow-hero', 'La signature du torréfacteur en une phrase et un appel à goûter.'),
                R('anchor-nav', 'Les noms des repères de la page, en deux mots chacun.'),
                R('featured-collection', 'Le nom de la sélection du moment et pourquoi elle est là.'),
                R('image-with-text', 'L’origine : le pays, la coopérative, ce qui rend ce lot différent.'),
                R('collection-list', 'Les familles de café, nommées simplement.'),
                R('custom-columns', 'Le rituel : mouture, dose, eau, temps.'),
                R('icon-bar', 'Fraîcheur de torréfaction, livraison, format, abonnement.'),
                R('newsletter', 'Une raison honnête de s’abonner à la lettre du torréfacteur.'),
            ],
            product: productPage({
                hero: 'Le nom du café, son origine, son profil de tasse en trois mots.',
                proof: 'Torréfaction récente, expédition, retour. Rien d’invérifiable.',
                detail: 'Les notes en tasse, la méthode d’extraction conseillée, la mouture, la conservation.',
                objection: 'Les questions qui retiennent : intensité, acidité, machine compatible.',
                faq: 'Six questions réelles sur la préparation et la conservation.',
            }),
        },
    },
    {
        id: 'books',
        label: 'Livres & librairie',
        voice: 'La voix d’un libraire qui recommande. On donne envie de lire, on ne vend pas une fiche technique.',
        pages: {
            index: [
                R('slideshow-hero', 'Le parti pris de la librairie en une phrase.'),
                R('collection-list', 'Les rayons, nommés comme dans une vraie librairie.'),
                R('rich-text', 'Les premières lignes du livre du mois, ou le mot du libraire.'),
                R('image-with-text', 'Qui tient cette librairie et pourquoi.'),
                R('featured-collection', 'Le nom de la table de mise en avant et ce qui la relie.'),
                R('custom-columns', 'Les services : conseil, commande, retrait, envoi.'),
                R('newsletter', 'Une raison de recevoir les conseils de lecture.'),
            ],
            product: productPage({
                hero: 'Le titre, l’auteur, et en une phrase pourquoi le lire.',
                proof: 'Expédition soignée, retour, conseil. Rien d’invérifiable.',
                detail: 'Le sujet, le ton, à qui il s’adresse, le format et la pagination.',
                objection: 'Les questions qui retiennent : niveau de lecture, langue, édition.',
                faq: 'Six questions réelles sur l’édition, l’envoi et la commande.',
            }),
        },
    },
    {
        id: 'toys',
        label: 'Jouets & enfants',
        voice: 'Direct et rassurant, écrit pour un parent pressé. Jamais de langage commercial agressif.',
        pages: {
            index: [
                R('slideshow-hero', 'Ce que la boutique propose et pour quel âge, en une phrase.'),
                R('collection-list', 'Les rayons par âge ou par usage.'),
                R('shoppable-image', 'Les libellés des points cliquables sur la scène de jeu.'),
                R('featured-collection', 'La sélection du moment et pourquoi elle est là.'),
                R('icon-bar', 'Sécurité, âge conseillé, matériaux, livraison.'),
                R('image-with-text', 'Ce que la boutique choisit et refuse de vendre.'),
                R('custom-columns', 'Les repères pour choisir : âge, durée de jeu, encombrement.'),
                R('newsletter', 'Une raison honnête de laisser son adresse.'),
            ],
            product: productPage({
                hero: 'Le jouet, l’âge conseillé, ce que l’enfant en fait.',
                proof: 'Sécurité, retour, livraison. Rien d’invérifiable.',
                detail: 'Les matériaux, la taille, ce qu’il développe, ce qu’il y a dans la boîte.',
                objection: 'Les questions des parents : solidité, bruit, piles, nettoyage.',
                faq: 'Six questions réelles de parents.',
            }),
        },
    },
    {
        id: 'supplements',
        label: 'Compléments & nutrition',
        voice: 'Sobre et factuel. On décrit une composition et un usage — jamais un bénéfice santé, jamais un chiffre d’étude.',
        pages: {
            index: [
                R('slideshow-hero', 'Ce que fait la marque, en une phrase sans promesse de santé.'),
                R('icon-bar', 'Composition, fabrication, contrôle, livraison.'),
                R('featured-collection', 'Le nom de la gamme mise en avant.'),
                R('image-with-text', 'Pourquoi la marque existe et comment elle formule.'),
                R('collection-list', 'Les familles de produits.'),
                R('custom-columns', 'Les engagements : provenance, dosage, transparence.'),
                R('newsletter', 'Une raison honnête de s’inscrire.'),
            ],
            product: productPage({
                hero: 'Le produit, sa forme, à quel moment on le prend.',
                proof: 'Fabrication, contrôle, retour, livraison. Rien d’invérifiable.',
                detail: 'La composition, la posologie, le format, la durée d’une boîte.',
                objection: 'Les questions qui retiennent : goût, allergènes, association, grossesse.',
                faq: 'Six questions réelles sur la prise et la composition.',
            }),
        },
    },
    {
        id: 'tech',
        label: 'Électronique & audio',
        voice: 'Net et technique, sans jargon inutile. On explique un usage avant une caractéristique.',
        pages: {
            index: [
                R('slideshow-hero', 'Ce que fait l’appareil, en une phrase, et pour qui.'),
                R('trust-row', 'Garantie, retour, service. Rien d’invérifiable.'),
                R('collection-list', 'Les familles de produits.'),
                R('featured-collection', 'La sélection mise en avant et son fil conducteur.'),
                R('multirow', 'Les usages concrets, un par ligne.'),
                R('tabbed-content', 'Les onglets d’usage : au quotidien, en déplacement, à la maison.'),
                R('custom-columns', 'Les points forts, un par colonne, en langage d’usage.'),
                R('image-with-text', 'Ce que la marque cherche à résoudre.'),
                R('faq', 'Six questions réelles sur la compatibilité et l’usage.'),
                R('newsletter', 'Une raison honnête de s’inscrire.'),
            ],
            product: productPage({
                hero: 'L’appareil, ce qu’il permet de faire, pour qui.',
                proof: 'Garantie, retour, service après-vente. Rien d’invérifiable.',
                detail: 'Les usages, la compatibilité, ce qu’il y a dans la boîte, la prise en main.',
                objection: 'Les questions qui retiennent : compatibilité, autonomie, réparabilité.',
                faq: 'Six questions réelles sur l’installation et la compatibilité.',
            }),
        },
    },
    {
        id: 'homeware',
        label: 'Maison & art de vivre',
        voice: 'Posé et matériel. On parle de matière, d’usage et de durée, pas de tendance.',
        pages: {
            index: [
                R('slideshow-hero', 'Ce que la boutique défend, en une phrase.'),
                R('collection-list', 'Les pièces de la maison, nommées simplement.'),
                R('featured-collection', 'La sélection du moment et son fil conducteur.'),
                R('image-with-text', 'L’atelier, la matière, la façon de travailler.'),
                R('icon-bar', 'Matière, fabrication, entretien, livraison.'),
                R('custom-columns', 'Les repères pour choisir : dimension, matière, entretien.'),
                R('newsletter', 'Une raison honnête de laisser son adresse.'),
            ],
            product: productPage({
                hero: 'L’objet, sa matière, où il trouve sa place.',
                proof: 'Fabrication, retour, livraison. Rien d’invérifiable.',
                detail: 'La matière, les dimensions, l’usage quotidien, l’entretien.',
                objection: 'Les questions qui retiennent : taille réelle, entretien, résistance.',
                faq: 'Six questions réelles sur l’usage et l’entretien.',
            }),
        },
    },
    {
        id: 'default',
        themeId: 'homeware',
        label: 'Autre',
        voice: 'Clair et concret. On dit ce que le produit fait et pour qui, sans superlatif.',
        pages: {
            index: [
                R('slideshow-hero', 'Ce que vend la boutique, en une phrase.'),
                R('hero', 'Ce que vend la boutique, en une phrase.'),
                R('collection-list', 'Les rayons, nommés simplement.'),
                R('featured-collection', 'La sélection mise en avant.'),
                R('icon-bar', 'Les raisons d’acheter ici : livraison, retour, service, paiement.'),
                R('image-with-text', 'Pourquoi la boutique existe.'),
                R('custom-columns', 'Les points forts, un par colonne.'),
                R('faq', 'Six questions réelles d’acheteur.'),
                R('newsletter', 'Une raison honnête de s’inscrire.'),
            ],
            product: productPage({
                hero: 'Le produit, ce qu’il fait, pour qui.',
                proof: 'Livraison, retour, paiement. Rien d’invérifiable.',
                detail: 'Les caractéristiques utiles, traduites en bénéfices concrets.',
                objection: 'Les questions qui retiennent d’acheter.',
                faq: 'Six questions réelles d’acheteur.',
            }),
        },
    },
];
const BY_ID = new Map(RECIPES.map((r) => [r.id, r]));
/** The recipe for a category, falling back to the all-purpose one rather than generating nothing. */
export function getRecipe(categoryId) {
    return BY_ID.get(categoryId) ?? BY_ID.get('default');
}
/** The theme a category applies — the wizard's category→applyTheme wire. */
export function themeIdFor(categoryId) {
    const r = getRecipe(categoryId);
    return r.themeId ?? r.id;
}
/**
 * Turn a seeded theme document + a category into the ordered plan the generator executes.
 *
 * IN DOCUMENT ORDER, always. That order is what the construction scene reveals (0059 §5.2) and the order
 * the model reads: a hero briefed after the FAQ produces a page written backwards.
 *
 * Every section of the document appears in the plan, written or not, each with its reason. Nothing is
 * dropped on the floor — the caller needs the skipped ones to tell the seller which parts are his to
 * fill in, which is the whole difference between our output and a page still saying "About Us
 * Information" five times.
 */
export function planPage(document, recipe, sectionRegistry, blockRegistry = defaultBlockRegistry) {
    const template = document.template;
    const roles = new Map((recipe.pages[template] ?? []).map((r) => [r.type, r.role]));
    const sections = [];
    let writableSlots = 0;
    for (const section of document.sections ?? []) {
        if (!section || typeof section.id !== 'string')
            continue;
        const base = { sectionId: section.id, type: section.type };
        if (section.disabled) {
            sections.push({ ...base, policy: 'keep', skipped: 'disabled', slots: [] });
            continue;
        }
        // INTEGRITY FIRST — checked before the recipe, so no category can opt back in.
        if (NEVER_GENERATED.includes(section.type)) {
            sections.push({ ...base, policy: 'never', skipped: 'integrity', slots: [] });
            continue;
        }
        // SLOTS BEFORE ROLE. A section with no prose (a divider, a spacer) is not a gap in the recipe — it
        // has nothing to say. Checking the recipe first buried 16 `section-divider` in the "uncovered"
        // list and hid the real misses among them.
        const slots = collectTextSlots({ ...document, sections: [section] }, sectionRegistry, blockRegistry);
        if (slots.length === 0) {
            sections.push({ ...base, policy: 'keep', skipped: 'no-slots', slots: [] });
            continue;
        }
        const role = roles.get(section.type) ?? UNIVERSAL_ROLES[section.type];
        if (!role) {
            sections.push({ ...base, policy: 'keep', skipped: 'not-in-recipe', slots: [] });
            continue;
        }
        sections.push({ ...base, policy: 'write', role, slots });
        writableSlots += slots.length;
    }
    return { template, voice: recipe.voice, sections, writableSlots };
}
//# sourceMappingURL=recipes.js.map